import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";
import { invariant } from "ts-invariant";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { bearAuthClient } from "@/lib/bear-auth-client";
import {
  type OrganizationSummary,
  RequireSignIn,
  useSession,
  useSignedInUser,
} from "@/lib/session";

export const Route = createFileRoute("/org")({
  component: () => (
    <RequireSignIn>
      <OrganizationPage />
    </RequireSignIn>
  ),
});

type Role = "owner" | "admin" | "member";

const ROLES: { value: Role; label: string; description: string }[] = [
  {
    value: "owner",
    label: "Owner",
    description: "Everything an admin can do, and can't be the last to leave.",
  },
  {
    value: "admin",
    label: "Admin",
    description: "Manages members, invitations, project sharing and deletion.",
  },
  {
    value: "member",
    label: "Member",
    description: "Sees, edits, and uses AI on every project here.",
  },
];

const isRole = (value: string): value is Role =>
  ROLES.some((role) => role.value === value);

/** A role picked from `ROLES`, so anything else is a bug. */
function roleOf(value: string): Role {
  invariant(isRole(value), `unknown role ${value}`);
  return value;
}

const hasRole = (role: string, wanted: Role[]) =>
  role.split(",").some((r) => {
    const trimmed = r.trim();
    return isRole(trimmed) && wanted.includes(trimmed);
  });

function OrganizationPage() {
  const { organization } = useSession();
  if (!organization) {
    return (
      <main className="mx-auto flex min-h-screen max-w-2xl flex-col gap-4 px-6 py-12">
        <p>You don&apos;t belong to an organization yet.</p>
        <Link to="/" className="text-sm underline">
          Back to projects
        </Link>
      </main>
    );
  }
  return <OrganizationSettings key={organization.id} org={organization} />;
}

function OrganizationSettings({ org }: { org: OrganizationSummary }) {
  const user = useSignedInUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const full = useQuery({
    queryKey: ["organization", org.id],
    queryFn: async () => {
      const result = await bearAuthClient.organization.getFullOrganization({
        query: { organizationId: org.id },
      });
      if (result.error) throw new Error(result.error.message);
      invariant(result.data, "organization not found");
      return result.data;
    },
  });
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["organization", org.id] });

  const me = full.data?.members.find((m) => m.userId === user.id);
  const canAdminister = me ? hasRole(me.role, ["owner", "admin"]) : false;

  async function run(
    action: () => Promise<{ error: { message?: string } | null }>,
    failure: string,
  ) {
    const result = await action();
    if (result.error) {
      toast.error(result.error.message ?? failure);
      return false;
    }
    await refresh();
    return true;
  }

  async function leave() {
    const left = await run(
      () => bearAuthClient.organization.leave({ organizationId: org.id }),
      "Could not leave the organization.",
    );
    if (left) await navigate({ to: "/" });
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-2xl flex-col gap-10 px-6 py-12">
      <Link to="/" className="text-sm text-muted-foreground underline">
        Back to projects
      </Link>
      <section className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold">{org.name}</h1>
        <p className="text-sm text-muted-foreground">
          Everyone here can see, edit, and use AI on all of this
          organization&apos;s projects. To share a project with this
          organization, give its owner this organization ID:
        </p>
        <div className="flex items-center gap-2">
          <Input readOnly value={org.slug} className="font-mono" />
          <Button
            variant="outline"
            onClick={() => void navigator.clipboard.writeText(org.slug)}
          >
            Copy
          </Button>
        </div>
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold">Members</h2>
        <ul className="divide-y rounded-lg border">
          {full.data?.members.map((member) => (
            <li key={member.id} className="flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {member.user.name}
                  {member.userId === user.id ? " (you)" : ""}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {member.user.email}
                </p>
              </div>
              {canAdminister ? (
                <Select
                  value={member.role}
                  onValueChange={(role) =>
                    void run(
                      () =>
                        bearAuthClient.organization.updateMemberRole({
                          memberId: member.id,
                          role: roleOf(role),
                          organizationId: org.id,
                        }),
                      "Could not change the role.",
                    )
                  }
                >
                  <SelectTrigger className="w-32" aria-label="Role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-sm text-muted-foreground">
                  {ROLES.find((r) => r.value === member.role)?.label ??
                    member.role}
                </span>
              )}
              {canAdminister && member.userId !== user.id ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    void run(
                      () =>
                        bearAuthClient.organization.removeMember({
                          memberIdOrEmail: member.id,
                          organizationId: org.id,
                        }),
                      "Could not remove the member.",
                    )
                  }
                >
                  Remove
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
        <dl className="grid gap-1 text-xs text-muted-foreground">
          {ROLES.map((role) => (
            <div key={role.value}>
              <dt className="inline font-medium text-foreground">
                {role.label}:
              </dt>{" "}
              <dd className="inline">{role.description}</dd>
            </div>
          ))}
        </dl>
      </section>

      {canAdminister ? (
        <Invitations
          organizationId={org.id}
          invitations={(full.data?.invitations ?? []).filter(
            (i) => i.status === "pending",
          )}
          run={run}
        />
      ) : null}

      <section className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold">Leave {org.name}</h2>
        <p className="text-sm text-muted-foreground">
          You lose access to its projects unless they&apos;re also shared with
          you. Its projects stay with the organization.
        </p>
        <div>
          <Button variant="outline" onClick={() => void leave()}>
            Leave organization
          </Button>
        </div>
      </section>
    </main>
  );
}

interface PendingInvitation {
  id: string;
  email: string;
  role: string | null;
  expiresAt: Date | string;
}

function Invitations({
  organizationId,
  invitations,
  run,
}: {
  organizationId: string;
  invitations: PendingInvitation[];
  run: (
    action: () => Promise<{ error: { message?: string } | null }>,
    failure: string,
  ) => Promise<boolean>;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("member");
  const linkFor = (id: string) => `${window.location.origin}/invite/${id}`;

  async function invite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const sent = await run(
      () =>
        bearAuthClient.organization.inviteMember({
          email: email.trim(),
          role,
          organizationId,
        }),
      "Could not create the invitation.",
    );
    if (sent) setEmail("");
  }

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold">Invitations</h2>
        <p className="text-sm text-muted-foreground">
          Bear emails each invitation with a link that signs the invitee in. You
          can also copy the link. Only someone signed in with the invited email
          can accept it.
        </p>
      </div>
      <form className="flex gap-2" onSubmit={invite}>
        <Input
          aria-label="Email to invite"
          type="email"
          required
          placeholder="name@company.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Select value={role} onValueChange={(value) => setRole(roleOf(value))}>
          <SelectTrigger className="w-32" aria-label="Invited role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ROLES.map((r) => (
              <SelectItem key={r.value} value={r.value}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="submit">Invite</Button>
      </form>
      {invitations.length > 0 ? (
        <ul className="divide-y rounded-lg border">
          {invitations.map((invitation) => (
            <li key={invitation.id} className="flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {invitation.email}
                </p>
                <p className="text-xs text-muted-foreground">
                  Expires {new Date(invitation.expiresAt).toLocaleDateString()}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  void navigator.clipboard
                    .writeText(linkFor(invitation.id))
                    .then(() => toast.success("Invitation link copied"))
                }
              >
                Copy link
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  void run(
                    () =>
                      bearAuthClient.organization.cancelInvitation({
                        invitationId: invitation.id,
                      }),
                    "Could not cancel the invitation.",
                  )
                }
              >
                Cancel
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
